/**
 * App-side wiring for the voice-assistant store.
 *
 * ONE input crosses, and it crosses because the store cannot resolve it without
 * reaching for the app: `isVoiceWorkspaceEnabled()` reads the domain-scoped
 * OpenFeature client and the force-enable-all build override, both of which
 * `@heidi/agents` bans from its UI zone — the flag mechanism by zone policy and
 * the env read package-wide. `workspaceFlag.ts` IS the app resolver and stays
 * app-side; what the store takes is a reader of it.
 *
 * A THUNK, never a boolean. The store calls it at the point it used to call the
 * resolver directly, so a flag flip mid-session changes the next read exactly as
 * it does today. Handing over `isVoiceWorkspaceEnabled()` — the value — would
 * freeze whatever the flag said when this module was evaluated, which for a
 * module-scope wiring is "before the OpenFeature provider has resolved
 * anything".
 *
 * THE STORE IMPORT IS DEFERRED, AND THAT IS THE WHOLE SHAPE OF THIS MODULE.
 * `_app.tsx` imports this for effect at module scope, so a static
 * `import { configureVoiceAssistantStore }` would pull the store — and, through
 * the `mapEvidenceToInfoCard` delegate under it, the whole `@heidi/agents` UI
 * door — into the app's eager graph. MEASURED, not assumed:
 * `npx jest src/utils/voice-assistant-store-wiring.test.ts` walks the runtime
 * (non-`import type`, non-dynamic) graph from `src/pages/_app.tsx` and pins the
 * result. That walk reads 2961 modules at `ae2cf3c21c`; a static import here took
 * it to 3202 and pulled `packages/agents/src/index.ts` in with it. Deferred it
 * reads 2963 — this module and the flag resolver, no package module — and the
 * door is absent. That is the class of regression
 * `packages/evidence/src/data/config.ts` records as a production TDZ during
 * "Collecting page data" — one that tsc, jest and lint are all blind to.
 *
 * The deferral cannot lose a read in practice, and the store reports it if it
 * ever does. `import()` is fired here at `_app` evaluation, before any user
 * interaction; the store's one flag read runs inside `upsertEvidence`, which
 * only a live realtime session drives. Reaching it needs the store module
 * loaded — and once it is, this promise has already settled from the module
 * registry. A read that beats it is logged once by the store rather than
 * answered as if the flag had spoken.
 */
import { isVoiceWorkspaceEnabled } from '@/features/voice-assistant/workspace/workspaceFlag';
import { logger } from '@heidi/runtime/logger';

/**
 * Settles when the store holds the reader. Exported so a test can await the
 * wiring instead of racing it; `_app` imports this module for effect only.
 */
export const voiceAssistantStoreWiring: Promise<void> =
  import('@/features/voice-assistant/store/useVoiceAssistantStore').then(
    ({ configureVoiceAssistantStore }) => {
      configureVoiceAssistantStore({ isVoiceWorkspaceEnabled });
    },
    (error: unknown) => {
      logger.error('[voice-assistant] store wiring failed to load', error);
    }
  );
